/** Serverzustand der Auditansicht -- F5-02. */

import { useQuery } from '@tanstack/react-query';

import { ApiError } from '../../lib/apiError';
import {
  fetchAuditActors,
  fetchAuditEntries,
  fetchAuditEntry,
  fetchAuditModules,
  PAGE_SIZE,
  type AuditFilters,
} from './api';

export const auditKeys = {
  all: ['audit'] as const,
  entries: (filters: AuditFilters, offset: number, limit: number) =>
    ['audit', 'entries', filters, offset, limit] as const,
  entry: (entryId: string) => ['audit', 'entry', entryId] as const,
  modules: ['audit', 'modules'] as const,
  actors: ['audit', 'actors'] as const,
};

const retryUnlessPermanent = (failureCount: number, error: unknown) =>
  !(error instanceof ApiError && error.isPermanent) && failureCount < 2;

export function useAuditEntries(
  filters: AuditFilters,
  offset = 0,
  { enabled = true, limit = PAGE_SIZE }: { enabled?: boolean; limit?: number } = {},
) {
  return useQuery({
    queryKey: auditKeys.entries(filters, offset, limit),
    queryFn: () => fetchAuditEntries(filters, { offset, limit }),
    enabled,
    retry: retryUnlessPermanent,
    // Das Protokoll waechst waehrend man hinsieht. Der vorige Stand bleibt
    // beim Blaettern stehen, statt die Liste kurz leer zu zeigen.
    placeholderData: (previous) => previous,
  });
}

export function useAuditEntry(entryId: string | null) {
  return useQuery({
    queryKey: auditKeys.entry(entryId ?? ''),
    queryFn: () => fetchAuditEntry(entryId ?? ''),
    enabled: entryId !== null,
    retry: retryUnlessPermanent,
  });
}

export function useAuditModules(enabled = true) {
  return useQuery({
    queryKey: auditKeys.modules,
    queryFn: fetchAuditModules,
    enabled,
    retry: retryUnlessPermanent,
  });
}

export function useAuditActors(enabled = true) {
  return useQuery({
    queryKey: auditKeys.actors,
    queryFn: fetchAuditActors,
    enabled,
    retry: retryUnlessPermanent,
  });
}
